/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { XIcon } from '@heroicons/react/outline';
import { INotification } from './useAlarmcenter';
import { Suspense, useRef } from 'react';
import { Spinner } from '@/components/Spinner';
import { format } from 'date-fns';
import { cn } from '@/utils/tailwind-util';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import api from '@/services/api/v2';
import { useObserver } from '../chat/hooks/useObserver';
import { useNavigate, useNavigation } from 'react-router-dom';
import { useChatStore } from '../chat';

interface Props {
  onClose?: () => void;
  readNotification: (id: number) => void;
  readAllNotifications: () => void;
}

const CATEGORY_MAP = {
  TASK: '업무',
  INQUIRY: '문의',
  NONE: '알림',
};

const AlarmcenterContent = ({
  onClose,
  // notifications,
  readNotification,
  readAllNotifications,
}: Props) => {
  const chatStore = useChatStore();
  const navigate = useNavigate();
  const $bottom = useRef<HTMLDivElement>(null);
  const {
    data: notifications,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['alarmcenter', 'page'],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await api.get(
        `/spation/notifications?page=${pageParam}&size=10`
      );
      return data.notifications;
    },
    getNextPageParam: (lastPage, pages) => {
      // return lastPage.last ? undefined : pages.length + 1;
      if (!lastPage) return undefined;
      else if (lastPage.last) return undefined;
      return lastPage.pageable.pageNumber + 1;
    },
  });

  const handleBottomIntersect: IntersectionObserverCallback = ([entry]) => {
    if (entry.isIntersecting && hasNextPage) fetchNextPage();
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleRead = (notification: INotification) => () => {
    if (!notification.isRead) {
      readNotification(notification.id);
    }
    switch (notification.category) {
      case 'TASK':
        if (notification?.payload?.date) {
          navigate(
            {
              pathname: '/report/not-uploaded',
            },
            {
              state: {
                day: `${notification?.payload?.date.split('-')[0]}-${
                  notification?.payload?.date.split('-')[1]
                }-${notification?.payload?.date.split('-')[2]}`,
                client: '',
              },
            }
          );
        } else if (
          notification?.payload?.clientId &&
          notification?.payload?.venueId
        ) {
          navigate(
            `/task/client/${notification.payload.clientId}/venue/${notification.payload.venueId}`
          );
        }
        if (onClose) onClose?.();
        break;
      case 'INQUIRY':
        if (
          notification?.payload?.clientId &&
          notification?.payload?.venueId &&
          notification?.payload?.groupId
        ) {
          if (window.matchMedia('(max-width: 768px)').matches) {
            navigate(
              `/chat/venue/${notification?.payload?.venueId}/group/${notification?.payload?.groupId}`
            );
          } else {
            navigate(
              `/chat/client/${notification?.payload?.clientId}/venue/${notification?.payload?.venueId}?groupId=${notification?.payload?.groupId}&my=true`
            );
            if (onClose) onClose?.();
          }
        }
        break;
      default:
        return;
    }
  };

  const handleReadAll = () => {
    readAllNotifications();
  };
  useObserver({
    target: $bottom,
    onIntersect: handleBottomIntersect,
  });

  if (!notifications) return null;
  return (
    <div className="flex flex-col z-[10000000] h-full py-5 overflow-hidden">
      <div className="flex items-center px-4 justify-between">
        <p className="text-xl font-bold">알림 내역</p>
        <button onClick={handleClose} className="max-md:hidden">
          <XIcon className="w-6 h-6" />
        </button>
      </div>
      <hr className="my-4" />
      <section className="flex flex-col px-4 flex-1 overflow-hidden">
        <div className="flex justify-end">
          <button onClick={handleReadAll} className="underline text-sm">
            모두 읽음
          </button>
        </div>
        <ul className="mt-2 gap-2 flex flex-1 flex-col overflow-y-auto">
          {notifications.pages
            .flatMap((_) => _.content)
            .map((notification, index) => (
              <li
                onClick={handleRead(notification)}
                key={notification.id}
                className={cn(
                  'flex p-2 text-sm cursor-pointer hover:bg-primary-200 rounded flex-col w-full min-h-fit border border-solid border-gray-300',
                  !notification.isRead && 'bg-primary-100'
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-gray-500 bg-gray-200 border border-gray-300 rounded px-2 py-1">
                      {CATEGORY_MAP[notification.category]}
                    </p>
                    <p className="text-gray-900 font-semibold truncate">
                      {notification.title}
                    </p>
                  </div>
                  <p className="text-xs text-gray-700 min-w-[100px] text-right">
                    {format(new Date(notification.createdAt), 'yy.MM.dd HH:mm')}
                  </p>
                </div>
                <p className="text-gray-800 min-h-fit mt-2 line-clamp-2">
                  {notification.body}
                </p>
              </li>
            ))}
          <div ref={$bottom} />
        </ul>
      </section>
    </div>
  );
};

export const Alarmcenter = (props: Props) => {
  return (
    <Suspense fallback={<Spinner />}>
      <AlarmcenterContent {...props} />
    </Suspense>
  );
};
